import React, { FC, useCallback, useMemo } from 'react';
import { Box, Button, Typography, Skeleton } from '@mui/material';
import {
	CreateEventTicketDto, EventPriceDto,
	EventPriceTypesEnum,
	EventTicketsReservationDto,
	EventTicketsTotalsWithReservationsDto,
	FileVariantsEnum,
	formatEventTimeRange,
	GetPricesAndFeesDto, DEFAULT_CURRENCY,
} from '@sweatpals/shared';
import { clsx, Colors, Showable, useStyles, OrderSummary } from '@sweatpals/react-ui-kit';
import { useFormikContext } from 'formik';
import { format, isValid } from 'date-fns';
import Image from 'next/legacy/image';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import { CheckIcon, EditIcon } from '@/modules/_core/icons';
import loader from '@/public/assets/images/loader.gif';
import PriceTierCard from '../price-tier-card/price-tier-card.component';
import { eventDetailsStyles } from './event-details.styles';
import { EventType } from '@/modules/_core/types/event-types';
import { useTimeToEventStart } from '@/modules/_core/hooks/use-time-to-event-start.hook';
import { CheckoutFormDto, EventPricesStepsDto, IntakeFormDto } from '@/modules/_core/interfaces/checkout-form.dto';
import { commonCheckoutFormStyles } from './checkout-form.styles';

interface EventDetailsProps {
	checkoutStep: number;
	ticketsQuantity: number;
	eventData?: EventType;
	publishedPrices?: EventPriceDto[];
	reservationTimer?: number | null;
	eventPricesSteps?: EventPricesStepsDto;
	showSkeleton?: boolean;
	promocode?: string;
	isPaying?: boolean;
	isPromocodeApplied?: boolean;
	ticketIsReserved?: boolean;
	isTiersEditable?: boolean;
	isPriceAndFeesLoading?: boolean;
	freeSpotsCount?: number;
	hideOrderDetails?: boolean;
	priceAndFees?: GetPricesAndFeesDto | null;
	firstIntakeForm?: IntakeFormDto;
	defaultIntakeForm?: IntakeFormDto;
	tickets?: CreateEventTicketDto[];
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto;
	userTicketReservations?: EventTicketsReservationDto[];
	editTiersDisabled?: boolean;
	clearTickets?(): void;
	onTicketAdd?(tierId?: string): void;
	onTicketRemove?(tierId?: string): void;
	onEditTiersButtonClick?(): void;
}

export const EventDetails: FC<EventDetailsProps> = (props) => {
	const {
		eventData,
		checkoutStep,
		isTiersEditable,
		isPriceAndFeesLoading,
		editTiersDisabled,
		ticketsQuantity,
		eventPricesSteps,
		reservationTimer,
		defaultIntakeForm,
		ticketIsReserved,
		firstIntakeForm,
		showSkeleton,
		publishedPrices,
		promocode,
		isPromocodeApplied,
		hideOrderDetails,
		isPaying,
		tickets,
		freeSpotsCount,
		priceAndFees,
		eventTicketsCountAndSpots,
		userTicketReservations = [],
		onTicketAdd,
		onTicketRemove,
		clearTickets,
		onEditTiersButtonClick,
	} = props;
	const {
		name: eventName,
		background,
		startDate,
		endDate,
		tzid,
		isDonationBased,
		prices,
		additionalData: additionalEventData,
		onlySingleAttendanceAllowed,
		author,
	} = eventData ?? {};
	const currency = author?.stripeConnectedAccountCurrency || DEFAULT_CURRENCY;

	const {
		values,
		isSubmitting,
		setValues,
	} = useFormikContext<CheckoutFormDto>();
	const { isPurchaseAllowed: isPurchaseAllowedDueToSalesStartTime } = useTimeToEventStart(eventData);
	const commonStyles = useStyles(commonCheckoutFormStyles);
	const styles = useStyles(eventDetailsStyles);

	const isOrderSummaryVisible = useMemo(
		() => ((eventData?.isPaid && !hideOrderDetails) || showSkeleton),
		[eventData?.isPaid, hideOrderDetails, showSkeleton]
	);

	const handleTicketAdd = useCallback((priceId: string): void => {
		const intakeForms = values.intakeForms ?? [];
		const foundPrice = prices.find(p => p.id === priceId);

		const ticketsPerGroup = !eventPricesSteps?.[priceId]
			? foundPrice?.minTicketsPerOrder ?? foundPrice?.ticketsPerGroup ?? 1
			: foundPrice?.ticketsPerGroup ?? 1;
		const newIntakeFormsCount = ticketsQuantity ? ticketsPerGroup : ticketsPerGroup - 1;

		const newIntakeForms = new Array<IntakeFormDto>(newIntakeFormsCount).fill(defaultIntakeForm);

		setValues({
			...values,
			intakeForms: [...intakeForms, ...newIntakeForms]
		});
		onTicketAdd(priceId);
	}, [defaultIntakeForm, eventPricesSteps, onTicketAdd, prices, setValues, ticketsQuantity, values]);

	const handleTicketRemove = useCallback((priceId: string): void => {
		const eventPriceSteps = eventPricesSteps?.[priceId];
		const foundPrice = prices.find(p => p.id === priceId);
		const priceTickets = tickets?.find(ticket => ticket.eventPriceId === priceId);

		if (!eventPriceSteps || !priceTickets || !foundPrice) {
			return;
		}

		const ticketsPerGroup = foundPrice.ticketsPerGroup ?? 1;
		const minTicketsPerOrder = foundPrice.minTicketsPerOrder ?? 1;
		const firstIntakeFormIndex = eventPriceSteps.firstStep - 1;
		const deleteIntakeFormsIndexes = Array.from(
			{ length: priceTickets.quantity },
			(_, index) => index + firstIntakeFormIndex
		).slice(minTicketsPerOrder >= priceTickets.quantity ? -priceTickets.quantity : -ticketsPerGroup);

		const newIntakeForms = (values.intakeForms ?? [])
			.filter((_, index) => !deleteIntakeFormsIndexes.includes(index));

		// Don't clear the first intake form
		newIntakeForms[0] = firstIntakeForm;

		setValues({
			...values,
			intakeForms: newIntakeForms,
		});

		onTicketRemove(priceId);
	}, [eventPricesSteps, onTicketRemove, prices, setValues, tickets, values, firstIntakeForm]);

	const onTicketSelect = useCallback((priceId: string): void => {
		setValues({
			...values,
			intakeForms: [(firstIntakeForm ?? { isPrefilled: false, result: {} })]
		});
		clearTickets();
		onTicketAdd(priceId);
	}, [clearTickets, firstIntakeForm, onTicketAdd, setValues, values]);

	return (
		<Box sx={styles.root}>
			<Box>
				<Box
					sx={clsx(styles.eventInfo, {
						borderRadius: isOrderSummaryVisible ? '8px 8px 0 0' : '8px'
					})}
				>
					<Showable show={showSkeleton}>
						<Skeleton
							sx={{ width: '70px', height: '70px', borderRadius: '12px', backgroundColor: Colors.white_a30 }}
							variant="rectangular"
						/>
						<Box sx={{ gap: '4px', display: 'flex', flexDirection: 'column', flex: 1 }}>
							<Skeleton
								sx={{ width: '138px', height: '20px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
								variant="rectangular"
							/>
							<Skeleton
								sx={{ width: '98px', height: '20px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
								variant="rectangular"
							/>
						</Box>
					</Showable>
					<Showable hide={showSkeleton}>
						{() => (
							<React.Fragment>
								{background ? (
									<Box
										sx={{
											...styles.eventBackground,
											backgroundImage: `url(${getPublicFileUrl(background, {
												variant: FileVariantsEnum.M
											})})`
										}}
									/>
								) : null}
								<Box sx={{ gap: '4px', display: 'flex', flexDirection: 'column' }}>
									<Box sx={styles.eventName}>{eventName}</Box>
									<Showable
										show={isValid(new Date(startDate)) && !additionalEventData?.hideEventDate}
									>
										<Typography sx={styles.eventDate}>
											{startDate ? formatEventTimeRange(startDate, endDate, {
												timezone: tzid,
												startDateFormat: 'eee MMM do @ h:mm aa',
												endDateFormat: 'eee MMM do @ h:mm aa',
											}) : ''}
										</Typography>
									</Showable>
								</Box>
							</React.Fragment>
						)}
					</Showable>
				</Box>
				<Showable show={isOrderSummaryVisible}>
					<Box position="relative" zIndex={1}>
						<svg
							style={{
								height: '1px',
								width: '100%',
								position: 'absolute',
								bottom: 0,
								left: 0,
								strokeWidth: '1px'
							}}
						>
							<line x1="0" y1="0" x2="100%" y2="0" stroke="#FFFFFF1A" strokeDasharray="6 6" />
						</svg>
						<Showable
							show={
								!eventData?.isDonationBased
								&& !editTiersDisabled
								&& checkoutStep === 1
								&& !showSkeleton
							}
						>
							<Button
								sx={styles.editTiersButton}
								disabled={
									(isPriceAndFeesLoading && !isTiersEditable)
									|| (!ticketsQuantity && isTiersEditable)
									|| isPaying || isSubmitting
								}
								onClick={onEditTiersButtonClick}
							>
								{isTiersEditable ? <CheckIcon /> : <EditIcon />}
								{isTiersEditable ? 'Save' : 'Edit'}
							</Button>
						</Showable>
					</Box>
					<Box sx={styles.orderSummary}>
						<Showable show={showSkeleton}>
							<Box
								sx={{
									p: '0 12px 1px',
									display: 'flex',
									flexDirection: 'column',
									gap: '18px',
								}}
							>
								<Box display="flex" justifyContent="space-between" height="14px">
									<Skeleton
										sx={{ height: '100%', width: '98px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '100%', width: '36px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '100%', width: '36px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
								</Box>
								<Box sx={{ height: '1px', width: '100%', backgroundColor: Colors.white_a10 }} />
								<Box display="flex" justifyContent="space-between" height="14px">
									<Skeleton
										sx={{ height: '100%', width: '98px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '100%', width: '36px', borderRadius: '20px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
								</Box>
								<Box display="flex" gap="4px" flexWrap="wrap">
									<Skeleton
										sx={{ height: '8px', width: '36px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '8px', width: '112px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '8px', width: '67px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '8px', width: '67px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '8px', width: '179px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
									<Skeleton
										sx={{ height: '8px', width: '136px', borderRadius: '8px', backgroundColor: Colors.white_a30 }}
										variant="rectangular"
									/>
								</Box>
							</Box>
						</Showable>
						<Showable hide={showSkeleton}>
							{() => (
								<React.Fragment>
									<Showable show={isPriceAndFeesLoading && !isTiersEditable}>
										<Box sx={commonStyles.loader}>
											<Image src={loader} width={64} height={64} alt={'google-play'} />
										</Box>
									</Showable>
									<Box
										sx={{
											height: { xs: 'auto', md: '100%' },
											overflow: { xs: 'unset', md: 'auto' },
											p: '0 12px 1px',
											opacity: isPriceAndFeesLoading && !isTiersEditable ? 0.2 : 1,
										}}
									>
										{isTiersEditable ? (
											<Box display="flex" flexDirection="column" gap="18px">
												{publishedPrices.map((price, index) => (
													<PriceTierCard
														currency={currency}
														key={price.id}
														index={index}
														isSmallSize
														hideWaitlist
														priceTier={price}
														freeSpotsCount={freeSpotsCount && (freeSpotsCount - ticketsQuantity)}
														eventTicketsCountAndSpots={eventTicketsCountAndSpots}
														userTicketReservations={userTicketReservations}
														isPurchaseAllowedDueToSalesStartTime={isPurchaseAllowedDueToSalesStartTime}
														onlySingleAttendanceAllowed={onlySingleAttendanceAllowed}
														quantity={tickets?.find(t => t.eventPriceId === price.id)?.quantity}
														onAdd={() => {
															handleTicketAdd(price.id);
														}}
														onRemove={() => {
															handleTicketRemove(price.id);
														}}
														onSubscribe={() => {
															onTicketSelect(price.id);
														}}
													/>
												))}
											</Box>
										) : (
											<OrderSummary
												tickets={tickets}
												prices={prices}
												promocode={promocode}
												isPromocodeApplied={isPromocodeApplied}
												priceAndFees={priceAndFees}
												isDonationBased={isDonationBased}
												priceNotIncludesTaxes={eventData?.additionalData?.priceNotIncludesTaxes}
											/>
										)}
									</Box>
								</React.Fragment>
							)}
						</Showable>
					</Box>
				</Showable>
			</Box>
			<Showable show={ticketIsReserved && reservationTimer && !showSkeleton}>
				{() => (
					<Box display="flex" justifyContent="center">
						<Typography sx={styles.reservationTimer}>
							Reservation time remaining
							{' '}
							<Box component="span">{format(reservationTimer * 1000, 'm:ss')}</Box>
						</Typography>
					</Box>
				)}
			</Showable>
		</Box>
	);
};
